import { orgCalendarDate } from '@/lib/utils/custom-date/custom-date';
import { workDayStartOf, workDaysOn } from '@/lib/utils/work-days/work-days';
import { WEEKDAYS } from '../schemas/working-info-schema';
import {
  Employee,
  HrTermInput,
  HrTermRecord,
  LeaveDetail,
  WorkDayInfo,
} from '../types/employee.types';

/** Row of the `UserHRPeriodicalProfile` schema, as queried by `buildGetHrProfilesQuery`. */
export interface HrProfileRow {
  ItemId: string;
  UserId?: string;
  HRPeriod?: number;
  IsCurrentHrYear?: boolean;
  EmploymentType?: string;
  HRPeriodStartDate?: string;
  HRPeriodEndDate?: string;
  IsFlexible?: boolean;
  AvgDailyWorkHour?: number;
  LeaveCarryForwardDays?: number;
  ToilCarryForwardDays?: number;
  SpecialToilCarryForwardDays?: number;
  /** The term's working days, embedded on the term (`[HRWorkDay]`). */
  WorkDays?: Partial<WorkDayInfo>[] | null;
  /** The term's leave allotment, one entry per leave category, embedded on the term (`[HRLeaveAllotment]`). */
  LeaveAllotments?: Partial<LeaveDetail>[] | null;
}

/** Row of the `UserContactDetail` schema, as queried by `buildGetContactDetailsQuery`. */
export type ContactDetailRow = { ItemId: string; UserId?: string } & Partial<
  Pick<Employee, (typeof CONTACT_DETAIL_FIELDS)[number]>
>;

/**
 * The `Employee` fields stored on the person's `UserContactDetail`; every other field of the basic info
 * and contact tabs is stored on their `UserProfileDetail`.
 */
export const CONTACT_DETAIL_FIELDS = [
  'PrivateEmail',
  'PrivatePhoneNumber',
  'IsPrivateInfoVisible',
  'EmergencyContact',
  'PresentAddress',
  'IsSameAsPresentAddress',
  'PermanentAddress',
  'LinkedInProfile',
  'GithubProfile',
  'FaceBookProfile',
  'WhatsAppProfile',
  'InstagramProfile',
  'ProfessionalImageUrl',
  'IsReferenceDetailsEnabled',
  'References',
  'BloodGroup',
  'FathersName',
  'MothersName',
  'SpouseName',
  'Nid',
  'Etin',
  'PassportNo',
  'PassportExpiryDate',
  'HobbiesInterests',
  'LatestAcademicTitle',
  'GraduationInstitute',
  'PostGraduationInstitute',
  'PreviousOrganization',
  'PreviousExperience',
] as const satisfies readonly (keyof Employee)[];

const CONTACT_DETAIL_FIELD_SET = new Set<string>(CONTACT_DETAIL_FIELDS);

/** Splits a tab's save into the fields of the `UserProfileDetail` and those of the `UserContactDetail`. */
export const splitProfileInput = (input: Record<string, unknown>) => {
  const profile: Record<string, unknown> = {};
  const contact: Record<string, unknown> = {};
  Object.entries(input).forEach(([key, value]) => {
    (CONTACT_DETAIL_FIELD_SET.has(key) ? contact : profile)[key] = value;
  });
  return { profile, contact };
};

/** The scope a person's records are stamped with, so department access rules see them. */
export interface EmployeeScope {
  userId?: string;
  departmentId?: string;
  departmentName?: string;
  departmentKey?: string;
}

/** Scope fields of `UserHRPeriodicalProfile` and `UserContactDetail` rows. */
export const toScopeInput = (scope: EmployeeScope) => ({
  ...(scope.userId ? { UserId: scope.userId } : {}),
  ...(scope.departmentId ? { DepartmentId: scope.departmentId } : {}),
  ...(scope.departmentName ? { DepartmentName: scope.departmentName } : {}),
  ...(scope.departmentKey ? { DepartmentKey: scope.departmentKey } : {}),
});

/** The scope of a saved employee (what their basic info says right now). */
export const scopeOf = (employee: Employee): EmployeeScope => ({
  userId: employee.UserId,
  departmentId: employee.DepartmentId,
  departmentName: employee.DepartmentName,
  departmentKey: employee.DepartmentKey,
});

/** Every HR term is named after its number, like the legacy `HR Term 1`. */
const hrPeriodName = (hrPeriod: number) => `HR Term ${hrPeriod}`;

/** One `UserHRPeriodicalProfile` row for one HR term of an employee, with its working days (every
 * work-day setting of the term) and leave allotment embedded (`WorkDays` / `LeaveAllotments`). Saving the
 * term replaces both lists whole. */
export const toHrProfileInput = (term: HrTermInput, scope: EmployeeScope) => ({
  ...toScopeInput(scope),
  HRPeriod: term.HRPeriod,
  HRPeriodName: hrPeriodName(term.HRPeriod),
  IsCurrentHrYear: term.IsCurrentHrYear,
  EmploymentType: term.EmploymentType,
  HRPeriodStartDate: term.HrPeriodStartDate,
  HRPeriodEndDate: term.HrPeriodEndDate,
  IsFlexible: term.IsFlexible,
  AvgDailyWorkHour: term.AvgDailyWorkHour,
  LeaveCarryForwardDays: term.LeaveCarryForwardDays,
  ToilCarryForwardDays: term.ToilCarryForwardDays,
  SpecialToilCarryForwardDays: term.SpecialToilCarryForwardDays,
  WorkDays: term.WorkDayHours.map(({ WorkDay, WorkingHours, EffectiveFrom }) => ({
    WorkDay,
    WorkingHours,
    ...(EffectiveFrom ? { EffectiveFrom } : {}),
  })),
  LeaveAllotments: term.LeaveDetails.map((detail) => ({
    LeaveCategoryId: detail.LeaveCategoryId,
    LeaveCategory: detail.LeaveCategory,
    LeaveLimitTotalDays: detail.LeaveLimitTotalDays,
    IsApplicable: detail.IsApplicable,
    IsUnlimited: detail.IsUnlimited,
    IsDefault: detail.IsDefault,
  })),
});

/** Work-day settings oldest first, each in weekday order. */
const bySettingThenWeekday = (a: WorkDayInfo, b: WorkDayInfo) =>
  workDayStartOf(a).localeCompare(workDayStartOf(b)) ||
  WEEKDAYS.indexOf(a.WorkDay) - WEEKDAYS.indexOf(b.WorkDay);

/** The term's working days (its embedded `WorkDays`, every setting), oldest setting first. */
const workDaysOf = (row: HrProfileRow): WorkDayInfo[] =>
  (row.WorkDays ?? [])
    .filter(
      (day): day is Partial<WorkDayInfo> & { WorkDay: string } => typeof day?.WorkDay === 'string'
    )
    .map(({ WorkDay, WorkingHours, EffectiveFrom }) => ({
      WorkDay,
      WorkingHours: WorkingHours ?? 0,
      ...(EffectiveFrom ? { EffectiveFrom } : {}),
    }))
    .sort(bySettingThenWeekday);

/** The term's leave allotment (its embedded `LeaveAllotments`). */
const leaveDetailsOf = (row: HrProfileRow): LeaveDetail[] =>
  (row.LeaveAllotments ?? [])
    .filter(
      (allotment): allotment is Partial<LeaveDetail> & { LeaveCategoryId: string } =>
        typeof allotment?.LeaveCategoryId === 'string'
    )
    .map((allotment) => ({
      LeaveCategoryId: allotment.LeaveCategoryId,
      LeaveCategory: allotment.LeaveCategory ?? '',
      LeaveLimitTotalDays: allotment.LeaveLimitTotalDays ?? 0,
      IsApplicable: allotment.IsApplicable ?? false,
      IsUnlimited: allotment.IsUnlimited ?? false,
      IsDefault: allotment.IsDefault ?? false,
    }));

const toHrTermRecord = (row: HrProfileRow, fallbackPeriod: number): HrTermRecord => ({
  ItemId: row.ItemId,
  HRPeriod: row.HRPeriod ?? fallbackPeriod,
  IsCurrentHrYear: row.IsCurrentHrYear ?? false,
  EmploymentType: row.EmploymentType,
  HrPeriodStartDate: row.HRPeriodStartDate,
  HrPeriodEndDate: row.HRPeriodEndDate,
  IsFlexible: row.IsFlexible,
  WorkDayHours: workDaysOf(row),
  AvgDailyWorkHour: row.AvgDailyWorkHour,
  LeaveDetails: leaveDetailsOf(row),
  LeaveCarryForwardDays: row.LeaveCarryForwardDays ?? 0,
  ToilCarryForwardDays: row.ToilCarryForwardDays ?? 0,
  SpecialToilCarryForwardDays: row.SpecialToilCarryForwardDays ?? 0,
});

type ProfileRecord = Omit<
  Employee,
  | 'WorkDays'
  | 'UserRoleKeys'
  | 'OrganizationAccess'
  | 'IsActive'
  | 'EmploymentType'
  | 'HrPeriodStartDate'
  | 'HrTerms'
>;

/** Every organization the employee's linked IAM user has access to, as read from `getIamUserOrganizationAccess`. */
export interface EmployeeOrganizationAccess {
  organizationAccess: { organizationId: string; roles: string[] }[];
}

const toHrTermRecords = (hrProfiles?: HrProfileRow[] | null): HrTermRecord[] =>
  (hrProfiles ?? []).map((row, index) => toHrTermRecord(row, index + 1));

/** The active term, else the latest one. */
const currentTermOf = (terms: HrTermRecord[]): HrTermRecord | undefined =>
  terms.find((term) => term.IsCurrentHrYear) ?? terms[terms.length - 1];

/** Day names the term works on today (its work-day setting in force today). */
const workDaysTodayOf = (term?: HrTermRecord): string[] =>
  workDaysOn(term?.WorkDayHours, orgCalendarDate(new Date().toISOString())).map(
    (day) => day.WorkDay
  );

/** The employee's work days today in their current HR term, the same as `Employee.WorkDays`. */
export const currentWorkDaysOf = (hrProfiles?: HrProfileRow[] | null): string[] =>
  workDaysTodayOf(currentTermOf(toHrTermRecords(hrProfiles)));

/**
 * Merges the person's records into the `Employee` view model. The flat HR fields mirror the active term
 * (or the latest one while none is active); the contact fields come from their `UserContactDetail`, and
 * each term's working days and leave allotment from the lists embedded on the term.
 */
export const toEmployee = (
  profile: ProfileRecord,
  hrProfiles?: HrProfileRow[] | null,
  access?: EmployeeOrganizationAccess | null,
  contact?: ContactDetailRow | null
): Employee => {
  const terms = toHrTermRecords(hrProfiles);
  const current = currentTermOf(terms);
  const organizationAccess = (access?.organizationAccess ?? []).map((organization) => ({
    OrganizationId: organization.organizationId,
    RoleKeys: organization.roles,
  }));
  const { ItemId: contactDetailItemId, UserId: _contactUserId, ...contactFields } = contact ?? {};
  return {
    ...contactFields,
    ...profile,
    ContactDetailItemId: contactDetailItemId,
    HrProfileItemId: current?.ItemId,
    HrTerms: terms,
    EmploymentType: current?.EmploymentType ?? '',
    HrPeriodStartDate: current?.HrPeriodStartDate ?? '',
    HrPeriodEndDate: current?.HrPeriodEndDate ?? undefined,
    IsFlexible: current?.IsFlexible,
    AvgDailyWorkHour: current?.AvgDailyWorkHour,
    WorkDays: workDaysTodayOf(current),
    UserRoleKeys: [...new Set(organizationAccess.flatMap((organization) => organization.RoleKeys))],
    OrganizationAccess: organizationAccess,
  };
};
